
import React, { useState, useMemo, useEffect } from "react"
import Highcharts from "highcharts"
import HighchartsReact from "highcharts-react-official"
import funnel from "highcharts/modules/funnel"
import exporting from "highcharts/modules/exporting"
import { Card, CardContent, Grid, Typography, TextField } from "@mui/material"
import { advanceSearch } from "../../../../_helper/services/advanceSearchv2"

// Initialize Highcharts modules
funnel(Highcharts)
exporting(Highcharts)

// Helper function to capitalize first letter
const capitalize = (s) => s && s.charAt(0).toUpperCase() + s.slice(1)

const CertificationCharts = ({ data }) => {
    const [startDate, setStartDate] = useState("")
    const [endDate, setEndDate] = useState("")
    // State for each chart's type
    const [funnelType, setFunnelType] = useState("funnel") // funnel, pie, doughnut, column, bar
    const [statusType, setStatusType] = useState("pie")   // pie, doughnut, column, bar
    const [deptCertType, setDeptCertType] = useState("pie") // pie, doughnut, column, bar
    const [vacancyType, setVacancyType] = useState("column") // column, bar, line, area

    // Memoized filter (your original code was already good here)
    const filteredData = useMemo(() => {
        if (!startDate || !endDate) return data
        const start = new Date(startDate)
        const end = new Date(endDate)
        end.setHours(23, 59, 59, 999)
        return data.filter(cert => {
            if (!cert.created_on) return false
            const certDate = new Date(cert.created_on)
            return certDate >= start && certDate <= end
        })
    }, [data, startDate, endDate])

    // ---- Data Processing ----

    // 1. Funnel/Status Data
    const totalApplicants = filteredData.reduce((sum, cert) => sum + (cert.TotalApp || 0), 0)
    const totalContacted = filteredData.reduce((sum, cert) => sum + (cert.Contacted || 0), 0)
    const totalScheduled = filteredData.reduce((sum, cert) => sum + (cert.Scheduled || 0), 0)
    const totalInterviewed = filteredData.reduce((sum, cert) => sum + (cert.Interviewed || 0), 0)
    const notYetContacted = Math.max(0, totalApplicants - totalContacted)

    // 2. Certifications by Department Data
    const certsByDept = useMemo(() => filteredData.reduce((acc, cert) => {
        const dept = cert.Dept_Name?.trim() || "Unknown Department"
        acc[dept] = (acc[dept] || 0) + 1
        return acc
    }, {}), [filteredData])

    // 3. Applicants vs Vacancies Data
    const { deptCategories, appData, vacancyData } = useMemo(() => {
        const byDept = filteredData.reduce((acc, cert) => {
            const dept = cert.Dept_Name?.trim() || "Unknown Department"
            if (!acc[dept]) {
                acc[dept] = { totalApp: 0, totalVacancy: 0 }
            }
            acc[dept].totalApp += cert.TotalApp || 0
            acc[dept].totalVacancy += cert.TotalVacancy || 0
            return acc
        }, {})
        const deptCategories = Object.keys(byDept)
        const appData = deptCategories.map(dept => byDept[dept].totalApp)
        const vacancyData = deptCategories.map(dept => byDept[dept].totalVacancy)
        return { deptCategories, appData, vacancyData }
    }, [filteredData])

    // ---- Chart Options ----

    // Helper to generate exporting menu items for switching types
    const generateMenuItems = (types, currentType, setType) => {
        const menuItems = ["viewFullscreen", "downloadPNG", "downloadPDF"];
        types.forEach(type => {
            if (type !== currentType) {
                menuItems.push({
                    text: `Switch to ${capitalize(type)}`,
                    onclick: () => setType(type)
                });
            }
        });
        return menuItems;
    };


    // ---- 1. Funnel Chart Options (Single-Series) ----
    const funnelOptions = {
        chart: { type: funnelType === 'doughnut' ? 'pie' : funnelType },
        title: { text: "Applicant Funnel" },
        plotOptions: {
            pie: { innerSize: funnelType === 'doughnut' ? '50%' : '0%' }
        },
        series: [{
            name: "Applicants",
            data: [
                ["Total Applicants", totalApplicants],
                ["Contacted", totalContacted],
                ["Scheduled", totalScheduled],
                ["Interviewed", totalInterviewed],
            ],
        }],
        exporting: {
            buttons: {
                contextButton: {
                    menuItems: generateMenuItems(
                        ['funnel', 'pie', 'doughnut', 'column',],
                        funnelType,
                        setFunnelType
                    )
                }
            }
        }
    }

    // ---- 2. Applicant Status Breakdown Options (Single-Series) ----
    const applicantStatusOptions = {
        chart: { type: statusType === 'doughnut' ? 'pie' : statusType },
        title: { text: "Applicant Status Breakdown" },
        tooltip: { pointFormat: "{series.name}: <b>{point.y}</b> ({point.percentage:.1f}%)" },
        plotOptions: {
            pie: {
                dataLabels: { enabled: true, format: "<b>{point.name}</b>: {point.y}" },
                innerSize: statusType === 'doughnut' ? '50%' : '0%'
            }
        },
        series: [{
            name: "Applicants",
            colorByPoint: true,
            data: [
                { name: "Not Yet Contacted", y: notYetContacted },
                { name: "Contacted", y: totalContacted },
                { name: "Scheduled", y: totalScheduled },
                { name: "Interviewed", y: totalInterviewed },
            ],
        }],
        exporting: {
            buttons: {
                contextButton: {
                    menuItems: generateMenuItems(
                        ['pie', 'doughnut', 'column',],
                        statusType,
                        setStatusType
                    )
                }
            }
        }
    }

    // ---- 3. Certifications by Department Options (Single-Series) ----
    const deptPieOptions = {
        chart: { type: deptCertType === 'doughnut' ? 'pie' : deptCertType },
        title: { text: "Certifications by Department" },
        tooltip: { pointFormat: "{series.name}: <b>{point.percentage:.1f}%</b>" },
        plotOptions: {
            pie: {
                dataLabels: { format: "{point.name}: {point.y}" },
                innerSize: deptCertType === 'doughnut' ? '50%' : '0%'
            }
        },
        series: [{
            name: "Certifications",
            colorByPoint: true,
            data: Object.entries(certsByDept).map(([name, y]) => ({ name, y })),
        }],
        exporting: {
            buttons: {
                contextButton: {
                    menuItems: generateMenuItems(
                        ['pie', 'doughnut', 'column',],
                        deptCertType,
                        setDeptCertType
                    )
                }
            }
        }
    }

    // ---- 4. Applicants vs Vacancies Options (Multi-Series) ----
    const vacancyOptions = {
        chart: { type: vacancyType },
        title: { text: "Applicants vs. Vacancies by Department" },
        xAxis: { categories: deptCategories, crosshair: true },
        yAxis: { min: 0, title: { text: "Count" } },
        tooltip: { shared: true, useHTML: true },
        plotOptions: { column: { pointPadding: 0.2, borderWidth: 0 } },
        series: [
            { name: "Total Applicants", data: appData },
            { name: "Total Vacancies", data: vacancyData },
        ],
        exporting: {
            buttons: {
                contextButton: {
                    menuItems: generateMenuItems(
                        ['column', 'line', 'area'],
                        vacancyType,
                        setVacancyType
                    )
                }
            }
        }
    }

    return (
        <Card>
            <CardContent>
                <Typography variant="h5" gutterBottom align="center">
                    Job Certification Dashboard
                </Typography>

                {/* --- FILTER CONTROLS --- */}
                <Grid container spacing={2} justifyContent="center" sx={{ mb: 4, p: 2, backgroundColor: '#f7f7f7', borderRadius: '8px' }}>
                    <Grid item xs={12} sm={4}>
                        <TextField
                            label="Start Date"
                            type="date"
                            value={startDate}
                            onChange={e => setStartDate(e.target.value)}
                            fullWidth
                            InputLabelProps={{ shrink: true }}
                        />
                    </Grid>
                    <Grid item xs={12} sm={4}>
                        <TextField
                            label="End Date"
                            type="date"
                            value={endDate}
                            onChange={e => setEndDate(e.target.value)}
                            fullWidth
                            InputLabelProps={{ shrink: true }}
                        />
                    </Grid>
                </Grid>

                {/* --- CHARTS GRID --- */}
                <Grid container spacing={3}>
                    <Grid item xs={12} md={6}>
                        <HighchartsReact highcharts={Highcharts} options={deptPieOptions} />
                    </Grid>
                    <Grid item xs={12} md={6}>
                        <HighchartsReact highcharts={Highcharts} options={vacancyOptions} />
                    </Grid>
                    <Grid item xs={12} md={6}>
                        <HighchartsReact highcharts={Highcharts} options={funnelOptions} />
                    </Grid>
                    <Grid item xs={12} md={6}>
                        <HighchartsReact highcharts={Highcharts} options={applicantStatusOptions} />
                    </Grid>
                </Grid>
            </CardContent>
        </Card>
    )
}

export default CertificationCharts